import React from 'react'
import cx from 'classnames'
import ScrollShadow from 'components/ScrollShadow'
import { clamp } from 'utils/math-util'
import {
  useFallbackNumberSpring,
  useScrollOffsetSpring,
} from 'hooks/spring-hooks'
import styles from './Card.sass'
import { to } from 'react-spring'

interface CardProps {
  /**
   * The stuff to render in this card
   */
  children?: React.ReactNode
  /**
   * Optional illustration to render in this card
   */
  illustration?: React.ReactNode
  /**
   * Optional attribute for testing purposes
   */
  dataTest?: string
  /**
   * Opt-out of max-height property
   */
  unconstrained?: boolean

  /**
   * Opt-out of border property
   */
  borderless?: boolean

  /**
   * Optional id
   */
  id?: string
}

interface CardScrollRefContextProps {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
}

export const CardScrollRefContext =
  React.createContext<CardScrollRefContextProps>({
    scrollRef: React.createRef(),
  })

export const Card = ({
  children,
  illustration,
  dataTest,
  unconstrained,
  borderless,
  id,
}: CardProps) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)

  return (
    <div
      className={cx(
        styles.root,
        !borderless && styles.bordered,
        unconstrained && styles.unconstrained,
      )}
      data-test={dataTest}
      id={id}
    >
      <div className={styles.cardContentContainer} ref={scrollRef}>
        <CardScrollRefContext.Provider value={{ scrollRef }}>
          {children}
        </CardScrollRefContext.Provider>
      </div>
      {illustration}
    </div>
  )
}

interface CardIllustrationProps {
  src: string
  alt?: string
}

/**
 * Card Illustration
 */
export function CardIllustration({ src, alt }: CardIllustrationProps) {
  return (
    <div className={styles.cardIllustration}>
      <img className={styles.cardIllustrationImage} src={src} alt={alt} />
    </div>
  )
}

const DELTA = 24

interface CardHeaderProps {
  children?: React.ReactNode
  compact?: boolean
  bordered?: boolean
  compactBottomPadding?: boolean
  fullWidth?: boolean
}

export const CardHeader = ({
  children,
  compact,
  bordered,
  compactBottomPadding,
  fullWidth,
}: CardHeaderProps) => {
  const { scrollRef } = React.useContext(CardScrollRefContext)
  const scrollOffset = useScrollOffsetSpring(scrollRef)

  const shadowOpacity = to(useFallbackNumberSpring(scrollOffset), (v) =>
    clamp(v / DELTA),
  )

  return (
    <div
      className={cx(
        styles.cardHeader,
        compact && styles.headerCompact,
        bordered && styles.headerBordered,
        compactBottomPadding && styles.compactBottomPadding,
        fullWidth && styles.fullWidth,
      )}
    >
      {children}
      <ScrollShadow lift borderless opacity={shadowOpacity} />
    </div>
  )
}

interface CardTitleProps {
  children?: React.ReactNode
  dataTest?: string
  cardLayout?: boolean
  fullWidth?: boolean
}

export const CardTitle = ({
  children,
  dataTest,
  cardLayout,
  fullWidth,
}: CardTitleProps) => {
  return (
    <div
      className={cx(
        styles.cardTitle,
        cardLayout && styles.marginTitle,
        fullWidth && styles.fullWidth,
      )}
      data-test={dataTest}
    >
      {children}
    </div>
  )
}

interface CardTitleCountProps {
  children?: React.ReactNode
}

export const CardTitleCount = ({ children }: CardTitleCountProps) => {
  return <span className={styles.cardTitleCount}>{children}</span>
}

interface CardHeaderActionsProps {
  children?: React.ReactNode
}

export const CardHeaderActions = ({ children }: CardHeaderActionsProps) => {
  return <div className={styles.cardHeaderActions}>{children}</div>
}

interface CardTabsProps {
  children?: React.ReactNode
  bordered?: boolean
}
export const CardTabs = ({ children, bordered }: CardTabsProps) => {
  return (
    <div className={cx(styles.cardTabs, bordered && styles.bordered)}>
      {children}
    </div>
  )
}

interface CardContentProps {
  children?: React.ReactNode
  compact?: boolean
  listContent?: boolean
  compactBottomPadding?: boolean
  autoHeight?: boolean
  scroll?: boolean
}

export const CardContent = ({
  children,
  compact,
  listContent,
  compactBottomPadding,
  autoHeight,
  scroll,
}: CardContentProps) => {
  return (
    <div
      className={cx(
        styles.cardContent,
        compact && styles.contentCompact,
        listContent && styles.listContent,
        compactBottomPadding && styles.cardContentCompactPaddingBottom,
        autoHeight && styles.cardContentAutoHeight,
        scroll && styles.scroll,
      )}
    >
      {children}
    </div>
  )
}

interface CardContentDescriptionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}
export function CardContentDescription({
  children,
  ...rest
}: CardContentDescriptionProps) {
  return (
    <div className={styles.cardContentDescription} {...rest}>
      {children}
    </div>
  )
}

interface CardFooterProps {
  children?: React.ReactNode
}

export const CardFooter = ({ children }: CardFooterProps) => {
  const { scrollRef } = React.useContext(CardScrollRefContext)
  const scrollOffset = useScrollOffsetSpring(scrollRef)

  const maxScroll = scrollRef.current
    ? scrollRef.current.scrollHeight - scrollRef.current.clientHeight
    : 0

  const shadowOpacity = to(useFallbackNumberSpring(scrollOffset), (v) =>
    clamp((maxScroll - v) / DELTA - 1),
  )

  return (
    <div className={styles.cardFooter}>
      <ScrollShadow placement="top" lift borderless opacity={shadowOpacity} />
      {children}
    </div>
  )
}

interface CardFooterContentProps {
  children?: React.ReactNode
}

export const CardFooterContent = ({ children }: CardFooterContentProps) => {
  return <div className={styles.cardFooterContent}>{children}</div>
}

interface CardFooterDescriptionProps {
  children?: React.ReactNode
}

export const CardFooterDescription = ({
  children,
}: CardFooterDescriptionProps) => {
  return <div className={styles.cardFooterDescription}>{children}</div>
}
